import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../hooks";
import { RoutesMap } from "../../types";
import { Role } from "../../types/roles/roleTypes";
import { platformTheme } from "../../styles/platformTheme";
import logo from "../../assets/image/nuevo_cfe_logo.png";
import "./Login.css";

type Values = {
  dni: string;
  password: string;
  perfil: number | undefined | null;
};

export const LoginView: React.FC<any> = (): JSX.Element => {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [data] = useState<Values>({
    dni: "",
    password: "",
    perfil: 1,
  });

  const validateForm = (values: Values) => {
    const errors: Partial<Record<keyof Values, string>> = {};
    const documentPattern = /^[0-9]{8,12}$/;

    if (!values.dni) errors.dni = "Documento requerido";
    if (values.dni && !documentPattern.test(values.dni)) {
      errors.dni = "Documento no válido";
    }
    if (!values.password) errors.password = "Contraseña requerida";

    return errors;
  };

  const onSubmit = async (values: Values) => {
    const { dni, password, perfil } = values;
    setLoading(true);
    setLoginError("");

    try {
      const success: any = await login({
        iduser_type: perfil ?? 1,
        password,
        dni,
      });
      if (!success?.status) return;

      const role = success?.detail?.userType;
      switch (role) {
        case Role.ADMIN:
          navigate(
            { pathname: redirect ?? RoutesMap.CYCLE },
            { replace: true }
          );
          break;
        case Role.AGENT:
          navigate({ pathname: RoutesMap.REWARDSTUDENT }, { replace: true });
          break;
        default:
          navigate({ pathname: redirect || "/dashboard" }, { replace: true });
          break;
      }
    } catch {
      setLoginError("No pudimos iniciar sesión. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={platformTheme}>
      <Box className="login-main-container">
        <Grid container className="login-grid">
          <Grid item xs={12} md={6} className="login-brand-panel">
            <Box className="login-brand-content">
              <Box
                component="img"
                src={logo}
                alt="Focus - Centro de Formación"
                className="login-logo"
              />
              <Typography className="login-eyebrow">
                Plataforma de gestión académica
              </Typography>
              <Typography variant="h3" className="login-brand-title">
                Formación que conecta talento y oportunidades.
              </Typography>
              <Typography className="login-brand-copy">
                Administra estudiantes, programas y certificaciones desde un
                espacio claro, seguro y centralizado.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} className="login-form-panel">
            <Paper component="section" elevation={0} className="login-card">
              <Stack spacing={0.75} mb={4}>
                <Typography variant="h4" color="primary.dark">
                  Bienvenido
                </Typography>
                <Typography color="text.secondary">
                  Ingresa tus datos para continuar a la plataforma.
                </Typography>
              </Stack>

              <Formik
                initialValues={data}
                enableReinitialize
                validate={validateForm}
                onSubmit={onSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleSubmit,
                  handleChange,
                }) => (
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2.5}>
                      <TextField
                        id="dni"
                        name="dni"
                        fullWidth
                        label="DNI o documento"
                        value={values.dni}
                        onChange={handleChange}
                        type="text"
                        autoComplete="username"
                        inputProps={{ maxLength: 12, inputMode: "numeric" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                        error={Boolean(errors.dni && touched.dni)}
                        helperText={errors.dni && touched.dni ? errors.dni : " "}
                      />

                      <TextField
                        id="password"
                        name="password"
                        fullWidth
                        label="Contraseña"
                        type={showPassword ? "text" : "password"}
                        value={values.password}
                        onChange={handleChange}
                        autoComplete="current-password"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon color="primary" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? "Ocultar contraseña"
                                    : "Mostrar contraseña"
                                }
                                onClick={() => setShowPassword((value) => !value)}
                                onMouseDown={(event) => event.preventDefault()}
                                edge="end"
                                color="primary"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        error={Boolean(errors.password && touched.password)}
                        helperText={
                          errors.password && touched.password
                            ? errors.password
                            : " "
                        }
                      />

                      <Button
                        type="submit"
                        disabled={loading}
                        fullWidth
                        size="large"
                        variant="contained"
                        sx={{ height: 48 }}
                      >
                        {loading ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          "Ingresar"
                        )}
                      </Button>

                      {loginError && (
                        <Typography
                          role="alert"
                          variant="body2"
                          color="error.main"
                          textAlign="center"
                        >
                          {loginError}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
              </Formik>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
                mt={4}
              >
                Focus - Centro de Formación
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};
